import {
  deferTracking,
  sendGAEvent,
  sendGALinkClickEvent,
} from '@noths/polaris-client-google-analytics';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';
import type { Price, ProductPartner } from '@noths/polaris-dev-ts-types';

import type { TrackingContext } from 'src/types/TrackingContext';

/**
 * The product fields the dataLayer payload is built from, declared in their own right rather than
 * picked off one service's response, so that products from either catalog source can produce it.
 * `partner` is narrowed to the two fields the payload reads.
 */
export interface TrackingProductData {
  code: number;
  freeDomesticDelivery: boolean;
  isNew: boolean;
  linkURL?: string;
  onSale: boolean;
  partner: Pick<ProductPartner, 'name' | 'shortcode'>;
  price: Price;
  purchasable: boolean;
  salePercentage: number | null;
  title: string;
}

export interface ProductImpressionTrackingContext extends TrackingContext {
  pageType?: string;
  products: TrackingProductData[];
}

export interface ProductClickTrackingContext extends TrackingContext {
  numberOfItems: number;
  pageType?: string;
  product: TrackingProductData;
  productIndex: number;
}

const getTrackedProduct = (product: TrackingProductData, index: number, list?: string) => {
  const {
    code,
    freeDomesticDelivery,
    isNew,
    onSale,
    partner,
    price,
    purchasable,
    salePercentage,
    title,
  } = product;
  const formattedPrice = String(price.amount / 100);

  const dimension22List = [
    freeDomesticDelivery && 'free uk delivery',
    isNew && 'new',
    onSale && !!salePercentage && `-${salePercentage}% off`,
    !purchasable && 'out of stock',
  ]
    .filter(Boolean)
    .join(', ');

  const position = index + 1;

  return {
    name: title,
    id: String(getProductIdFromProductCode(code)),
    dimension22: dimension22List,
    dimension25: position,
    dimension30: String(code),
    dimension31: formattedPrice,
    price: formattedPrice,
    dimension32: formattedPrice,
    dimension33: onSale ? `${salePercentage}.0%` : 'none',
    brand: partner.name,
    dimension34: partner.shortcode,
    position,
    ...(list && { list }),
    dimension16: 'not set',
  };
};

export const trackProductImpression = ({
  pageType = '',
  products,
  trackingContentType,
  trackingPosition,
  trackingTitle,
}: ProductImpressionTrackingContext) => {
  const numberOfItems = products.length;
  const list = `${pageType && pageType + ' - '}${trackingTitle}`.toLowerCase();

  // Defer off the critical path: impressions are fire-and-forget and do not gate navigation. Both pushes
  // run in the same deferred callback to preserve order (reset `ecommerce` before pushing the new object).
  deferTracking(() => {
    sendGAEvent({
      ecommerce: null,
    });

    sendGAEvent({
      event: 'product_impressions',
      event_category: `${trackingPosition} | ${trackingContentType}`,
      event_action: 'Product Impressions',
      event_label: `${trackingPosition} | ${trackingTitle}`.toLowerCase(),
      event_name: 'view_item_list',
      content_position: `${trackingPosition}`,
      content_type: `${trackingContentType}|${trackingTitle}`.toLowerCase(),
      total_options: `${numberOfItems}`,
      ecommerce: {
        impressions: products.map((product, index) => getTrackedProduct(product, index, list)),
      },
    });
  });
};

export const trackProductClick = (
  e: React.MouseEvent<HTMLAnchorElement>,
  {
    numberOfItems,
    pageType = '',
    product,
    productIndex,
    trackingContentType,
    trackingPosition,
    trackingTitle,
  }: ProductClickTrackingContext,
) => {
  sendGAEvent({
    ecommerce: null,
  });

  sendGALinkClickEvent(e, {
    event: 'product_clicks',
    event_category: `${trackingPosition} | ${trackingContentType}`,
    event_action: 'Product Click',
    event_label: `${trackingPosition} | ${trackingTitle}`.toLowerCase(),
    event_name: 'select_item',
    content_position: `${trackingPosition}`,
    content_type: `${trackingContentType}|${trackingTitle}`.toLowerCase(),
    total_options: `${numberOfItems}`,
    selected_position: `${productIndex + 1}`,
    item_id: product.code.toString(),
    ecommerce: {
      click: {
        actionField: {
          list: `${pageType && pageType + ' - '}${trackingTitle}`.toLowerCase(),
        },
        products: [getTrackedProduct(product, productIndex)],
      },
    },
  });
};
